import { useState, useEffect } from 'react';
import { MessageSquare, Check, X, Edit2, Trash2, Tag, AlertCircle, Paperclip } from 'lucide-react';
import toast from 'react-hot-toast';
import annotationsService from '../../services/annotations';
import MentionInput from './MentionInput';
import AttachmentUpload from './AttachmentUpload';
import AttachmentList from './AttachmentList';

export default function AnnotationsList({ targetType, targetId, projectId }) {
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [filterPriority, setFilterPriority] = useState('all');
  const [newAnnotation, setNewAnnotation] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newTags, setNewTags] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showAttachmentUpload, setShowAttachmentUpload] = useState({});

  useEffect(() => {
    loadAnnotations();
  }, [targetType, targetId, showResolved, filterPriority]);

  const loadAnnotations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (!showResolved) params.resolved = 'false';
      if (filterPriority !== 'all') params.priority = filterPriority;

      // Utiliser la route spécifique pour les projets
      let data;
      if (targetType === 'project' && projectId) {
        data = await annotationsService.getProjectAnnotations(projectId, params);
      } else {
        data = await annotationsService.getAnnotations(targetType, targetId, params);
      }
      setAnnotations(data);
    } catch (error) {
      console.error('Erreur chargement annotations:', error);
      toast.error('Erreur lors du chargement des annotations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newAnnotation.trim()) return;

    try {
      const tagsArray = newTags.split(',').map(t => t.trim()).filter(Boolean);
      await annotationsService.createAnnotation({
        content: newAnnotation,
        targetType,
        targetId,
        projectId,
        priority: newPriority,
        tags: tagsArray
      });

      setNewAnnotation('');
      setNewTags('');
      setNewPriority('medium');
      toast.success('Annotation ajoutée');
      loadAnnotations();
    } catch (error) {
      console.error('Erreur création annotation:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await annotationsService.updateAnnotation(id, updates);
      toast.success('Annotation mise à jour');
      setEditingId(null);
      loadAnnotations();
    } catch (error) {
      console.error('Erreur mise à jour annotation:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annotation ?')) return;

    try {
      await annotationsService.deleteAnnotation(id);
      toast.success('Annotation supprimée');
      loadAnnotations();
    } catch (error) {
      console.error('Erreur suppression annotation:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleAttachmentUpload = (annotationId) => {
    // Recharger l'annotation pour obtenir les nouveaux attachments
    loadAnnotations();
  };

  const handleAttachmentDelete = (annotationId, filename) => {
    // Recharger l'annotation
    loadAnnotations();
  };

  const toggleAttachmentUpload = (annotationId) => {
    setShowAttachmentUpload(prev => ({
      ...prev,
      [annotationId]: !prev[annotationId]
    }));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Haute';
      case 'medium': return 'Moyenne';
      case 'low': return 'Basse';
      default: return priority;
    }
  };

  // Mettre en surbrillance les mentions @username
  const highlightMentions = (content, mentionedUsers = []) => {
    if (!content || !mentionedUsers || mentionedUsers.length === 0) return content;

    let highlightedContent = content;

    mentionedUsers.forEach(user => {
      // Extraire le username de l'email (partie avant @)
      const username = user.email.split('@')[0];

      // Remplacer @username par un span avec style
      const mentionRegex = new RegExp(`@${username}\\b`, 'gi');
      highlightedContent = highlightedContent.replace(
        mentionRegex,
        `<span class="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-medium text-sm" title="${user.firstName} ${user.lastName}">@${username}</span>`
      );
    });

    return highlightedContent;
  };

  if (loading) {
    return <div className="text-center py-4">Chargement...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header avec filtres */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary-500" />
          Annotations ({annotations.length})
        </h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="rounded text-primary-600"
            />
            Afficher résolues
          </label>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="text-sm border-gray-300 rounded-lg"
          >
            <option value="all">Toutes priorités</option>
            <option value="high">Haute</option>
            <option value="medium">Moyenne</option>
            <option value="low">Basse</option>
          </select>
        </div>
      </div>

      {/* Formulaire nouvelle annotation */}
      <form onSubmit={handleCreate} className="bg-gray-50 rounded-lg p-4 space-y-3">
        <MentionInput
          value={newAnnotation}
          onChange={setNewAnnotation}
          placeholder="Ajouter une annotation... (Tapez @ pour mentionner)"
          rows={3}
        />
        <div className="flex items-center gap-3">
          <select
            value={newPriority}
            onChange={(e) => setNewPriority(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="low">Priorité basse</option>
            <option value="medium">Priorité moyenne</option>
            <option value="high">Priorité haute</option>
          </select>
          <input
            type="text"
            value={newTags}
            onChange={(e) => setNewTags(e.target.value)}
            placeholder="Tags (séparés par des virgules)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Ajouter
          </button>
        </div>
      </form>

      {/* Liste des annotations */}
      <div className="space-y-3">
        {annotations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Aucune annotation pour le moment
          </div>
        ) : (
          annotations.map((annotation) => (
            <div
              key={annotation.id}
              className={`bg-white border rounded-lg p-4 ${
                annotation.isResolved ? 'border-green-200 bg-green-50/30' : 'border-gray-200'
              }`}
            >
              {editingId === annotation.id ? (
                <div className="space-y-3">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleUpdate(annotation.id, { content: editContent })}
                      className="px-3 py-1 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                    >
                      Enregistrer
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(annotation.priority)}`}>
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        {getPriorityLabel(annotation.priority)}
                      </span>
                      {annotation.isResolved && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Check className="w-3 h-3 inline mr-1" />
                          Résolue
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!annotation.isResolved && (
                        <button
                          onClick={() => handleUpdate(annotation.id, { isResolved: true })}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Marquer comme résolue"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingId(annotation.id);
                          setEditContent(annotation.content);
                        }}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(annotation.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p
                    className="text-gray-700 mb-2"
                    dangerouslySetInnerHTML={{
                      __html: highlightMentions(annotation.content, annotation.mentionedUsers || [])
                    }}
                  />
                  {/* Afficher les utilisateurs mentionnés */}
                  {annotation.mentionedUsers && annotation.mentionedUsers.length > 0 && (
                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-600">
                      <span>Mentionné:</span>
                      {annotation.mentionedUsers.map((user, idx) => (
                        <span key={user.id} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                          {user.firstName} {user.lastName}
                        </span>
                      ))}
                    </div>
                  )}
                  {annotation.tags && annotation.tags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Tag className="w-3 h-3 text-gray-400" />
                      {annotation.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Afficher les pièces jointes */}
                  <AttachmentList
                    annotationId={annotation.id}
                    attachments={annotation.attachments || []}
                    onDelete={(filename) => handleAttachmentDelete(annotation.id, filename)}
                    canDelete={true}
                  />

                  {/* Bouton pour ajouter des pièces jointes */}
                  <div className="mt-3">
                    <button
                      onClick={() => toggleAttachmentUpload(annotation.id)}
                      className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <Paperclip className="w-3 h-3" />
                      {showAttachmentUpload[annotation.id] ? 'Masquer upload' : 'Ajouter pièces jointes'}
                    </button>
                    {showAttachmentUpload[annotation.id] && (
                      <div className="mt-2">
                        <AttachmentUpload
                          annotationId={annotation.id}
                          onUploadComplete={() => handleAttachmentUpload(annotation.id)}
                        />
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mt-3">
                    Par {annotation.user?.firstName} {annotation.user?.lastName} •{' '}
                    {new Date(annotation.createdAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
